<template>
    <main class="mb-5 pb-5">
        <div class="my-8">
            <h1 class="text-3xl text-theme-base capitalize">
                {{ itemComponent.firstName }} {{ itemComponent.lastName }}
            </h1>
            <p class="text-sm text-theme-muted mt-1 flex items-center gap-1">
                #<uuid-display :value="itemComponent.id" />
            </p>
        </div>
        <custom-navbar
            v-if="id"
            id="tabs-products"
            v-model="activeTab"
            center-collapsed
            not-line
        >
            <template #left>
                <custom-navbar-item id="home">
                    <router-link
                        to="/app/users"
                        class="rounded-full bg-brand-blue flex justify-center items-center p-2"
                    >
                        <span>🏠</span>
                    </router-link>
                </custom-navbar-item>
                <custom-navbar-item
                    id="general"
                    :active="['general', 'home'].includes(activeTab)"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden">
                            Información general
                        </span>
                        <span>&nbsp;⚙</span>
                    </div>
                </custom-navbar-item>

                <custom-navbar-item
                    id="security"
                    :active="activeTab === 'security'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Seguridad </span>
                        <span>&nbsp;🔐</span>
                    </div>
                </custom-navbar-item>
                <custom-navbar-item
                    id="merchants"
                    :active="activeTab === 'merchants'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Comercios </span>
                        <span>&nbsp;🌃</span>
                    </div>
                </custom-navbar-item>
            </template>
            <template #right>
                <custom-switch
                    v-if="$ability.hasPermission('user:Update')"
                    v-model="enabledUpdate"
                >
                    <span class="text-theme-inverse">🖊</span>
                </custom-switch>
            </template>
        </custom-navbar>

        <div v-show="['general', 'home'].includes(activeTab)">
            <h3 class="text-theme-primary my-3 text-lg">Datos básicos 👩‍🦱</h3>
            <div class="flex flex-wrap mt-5 gap-y-4">
                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.email"
                        label="Email"
                        disabled
                    />
                </div>
                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.uid"
                        label="Id externo"
                        disabled
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.tenantName"
                        label="Tenant"
                        disabled
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.firstName"
                        label="Nombres"
                        :disabled="!enabledUpdate"
                    />
                </div>
                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.lastName"
                        label="Apellidos"
                        :disabled="!enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.phone"
                        label="Celular de contacto"
                        type="number"
                        :disabled="!enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 my-4 mx-auto">
                    <checkbox-selector
                        v-model="itemComponent.needOnboarding"
                        label="Necesita onboarding"
                        :enabled="enabledUpdate"
                    />
                </div>
            </div>
            <div class="flex justify-center my-4">
                <app-button
                    :disabled="!enabledUpdate"
                    @click="updateBasicDataUser"
                >
                    Actualizar información básica
                </app-button>
            </div>
        </div>

        <div v-if="activeTab === 'merchants'">
            <h3 class="text-theme-primary my-3 text-lg">
                Alamcénes asociados 🌃
            </h3>
            <div class="w-8/12 pt-4">
                <merchants-role-selector
                    v-model="itemComponent.merchants"
                    :enabled="enabledUpdate"
                />
            </div>
            <div
                v-if="
                    $ability.hasPermission('user:UpdateMerchants') &&
                    enabledUpdate
                "
                class="w-full flex justify-center"
            >
                <app-button
                    :loading="isLoadingMerchants"
                    class="mt-4"
                    @click="updateMerchants"
                >
                    Actualizar accesos a almacenes
                </app-button>
            </div>
        </div>

        <div v-if="activeTab === 'security'">
            <h3 class="text-theme-primary my-3 text-lg">Seguridad 🔐</h3>
            <div class="my-4">
                en esta sección puedes modficar los accesos de un usuario.
                Puedes modificar los roles de un usuario y reestablecer
                contraseña.
            </div>
            <div class="w-8/12 pt-4">
                <role-selector
                    v-model="itemComponent.roles"
                    :enabled="enabledUpdate"
                />
            </div>
            <div
                v-if="
                    $ability.hasPermission('user:UpdateRoles') && enabledUpdate
                "
                class="w-full flex justify-center"
            >
                <app-button
                    :loading="isLoadingRoles"
                    class="mt-4"
                    @click="updateRoles"
                >
                    Actualizar roles
                </app-button>
            </div>
            <div class="flex mt-4">
                <h4 class="mr-3">Permisos</h4>
                <app-button
                    icon
                    circle
                    color="brand-purple"
                    @click="reloadPermissions"
                >
                    <reload-icon class="text-white h-5 w-5" />
                </app-button>
            </div>
            <permissions-table
                v-show="permissions.length"
                :items="permissions"
                show-role
            />
        </div>
    </main>
</template>

<script>
import tabQuerySyncMixin from "@/mixins/tabQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import { validateConfirmationMessage } from "@/common/lib/transformers";

import ReloadIcon from "@/components/icons/ReloadIcon.vue";
import MerchantsRoleSelector from "@/components/merchants/MerchantsRoleSelector.vue";
import PermissionsTable from "@/components/permissions/PermissionsTable.vue";
import RoleSelector from "@/components/roles/RoleSelector.vue";
import CheckboxSelector from "@/components/utils/CheckboxSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import UuidDisplay from "@/components/utils/UuidDisplay.vue";

export default {
    name: "DetailUsers",
    components: {
        ReloadIcon,
        PermissionsTable,
        MerchantsRoleSelector,
        RoleSelector,
        CheckboxSelector,
        CustomInput,
        UuidDisplay
    },
    mixins: [tabQuerySyncMixin],
    data: () => ({
        itemComponent: {
            id: "4",
            uid: "4",
            needOnboarding: false,
            firstName: "...",
            lastName: "...",
            tenantName: "...",
            email: "name@domain.com",
            phone: "...",
            checked: true,
            active: true,
            roles: [],
            merchants: []
        },
        isLoadingRoles: false,
        isLoadingMerchants: false,
        activeTab: "general",
        $tabsWhitelist: ["general", "security", "merchants"],
        enabledUpdate: false,
        isLoading: false,
        permissions: [],
        id: "0"
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"])
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if ("id" in this.$route.params) {
            if (
                !(await verifyPagePermission(
                    "user:Get",
                    "/app/users",
                    false,
                    true
                ))
            )
                return;
            const id = this.$route.params.id;
            this.id = id;
            await this.getBaseUser(id);
        }
    },
    methods: {
        ...mapActions("users", [
            "updateUser",
            "getUserById",
            "updateUserRoles",
            "updateUserMerchants",
            "getUserPermissionsById"
        ]),
        async getBaseUser(id) {
            this.isLoading = true;
            this.$loading.show();
            try {
                this.itemComponent = await this.getUserById({ id });
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app");
                }
            }
        },
        async updateBasicDataUser() {
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateUser(this.itemComponent);
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async updateRoles() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                icon: "info",
                title: "Estas a punto de cambiar los roles y por ende los permisos de un usuario.",
                text: "Recuerda un usuario solo puede tener dos roles y no se puede asignar rol super, eres consiente de lo que estas haciendo ?. Escribe 'Si' para actualizar",
                input: "text",
                confirmButtonColor: "#ee3f59",
                cancelButtonColor: "#253C99",
                showCancelButton: true
            });
            if (
                dialogResult.isConfirmed &&
                validateConfirmationMessage(dialogResult.value)
            ) {
                this.$loading.show();
                try {
                    await this.updateUserRoles({
                        userId: this.itemComponent.id,
                        rolesSlugs: this.itemComponent.roles.map(
                            (item) => item.slug
                        )
                    });
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$sound.play();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Actualización exitosa",
                        icon: "success",
                        showCloseButton: true
                    });
                } catch (error) {
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$sound.play("error");
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async updateMerchants() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                icon: "info",
                title: `Estas a punto de actualizar el control del usuario  sobre un tienda.`,
                text: `Recuerda si el usuario tiene perfil admin o super no se necesita permiso sobre cada tienda. Escribe "Si" para confirmar.`,
                input: "text",
                confirmButtonColor: "#ee3f59",
                cancelButtonColor: "#253C99",
                showCancelButton: true
            });
            if (
                dialogResult.isConfirmed &&
                validateConfirmationMessage(dialogResult.value)
            ) {
                this.$loading.show();
                try {
                    await this.updateUserMerchants({
                        userId: this.itemComponent.id,
                        merchants: this.itemComponent.merchants.map(
                            ({ merchantId, merchantType }) => ({
                                merchantId,
                                merchantType
                            })
                        )
                    });
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$sound.play();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Actualización exitosa",
                        icon: "success",
                        showCloseButton: true
                    });
                } catch (error) {
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$sound.play("error");
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async reloadPermissions() {
            this.isLoading = true;
            this.$loading.show();
            try {
                const permissions = await this.getUserPermissionsById({
                    id: this.id
                });
                this.permissions = permissions.map((item) => ({
                    name: item.permissionName,
                    slug: item.permissionSlug,
                    role: item.roleName
                }));
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
